import type {
  ChannelManagerIntegrationAdapter,
  FiscalPrinterIntegrationAdapter,
  HousekeepingAppIntegrationAdapter,
  IdScannerIntegrationAdapter,
  KeyCardIntegrationAdapter,
  PaymentGatewayIntegrationAdapter,
  PosIntegrationAdapter,
  RevenueManagementIntegrationAdapter,
} from "./adapters.ts";
import type { IntegrationHealthCheckResult } from "./types.ts";

type MockBehavior = {
  failNextHealthCheck?: boolean;
  failNextOperation?: boolean;
  degraded?: boolean;
};

function buildHealthCheck(providerCode: string, behavior: MockBehavior): Promise<IntegrationHealthCheckResult> {
  if (behavior.failNextHealthCheck) {
    behavior.failNextHealthCheck = false;
    return Promise.resolve({
      status: "failed",
      checkedAt: new Date().toISOString(),
      message: `${providerCode} health check failed.`,
    });
  }

  return Promise.resolve({
    status: behavior.degraded ? "degraded" : "connected",
    checkedAt: new Date().toISOString(),
    message: behavior.degraded ? `${providerCode} is responding slowly.` : `${providerCode} is healthy.`,
  });
}

function throwIfNeeded(providerCode: string, behavior: MockBehavior, operation: string) {
  if (behavior.failNextOperation) {
    behavior.failNextOperation = false;
    throw new Error(`${providerCode} ${operation} failed.`);
  }
}

export function createMockPosAdapter(providerCode = "mock-pos", displayName = "Mock POS", behavior: MockBehavior = {}): PosIntegrationAdapter {
  return {
    type: "pos",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async postCharge(payload) {
      throwIfNeeded(providerCode, behavior, "postCharge");
      return {
        provider: providerCode,
        externalReference: `${providerCode}-charge-${payload.amount.toFixed(0)}`,
      };
    },
  };
}

export function createMockPaymentGatewayAdapter(providerCode = "mock-pay", displayName = "Mock Payments", behavior: MockBehavior = {}): PaymentGatewayIntegrationAdapter {
  return {
    type: "payment_gateway",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async charge(payload) {
      throwIfNeeded(providerCode, behavior, "charge");
      return {
        provider: providerCode,
        transactionId: `${providerCode}-txn-${payload.amount.toFixed(0)}`,
        status: "captured",
      };
    },
  };
}

export function createMockKeyCardAdapter(providerCode = "mock-key", displayName = "Mock Key Cards", behavior: MockBehavior = {}): KeyCardIntegrationAdapter {
  return {
    type: "key_card",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async encodeKey(payload) {
      throwIfNeeded(providerCode, behavior, "encodeKey");
      return {
        provider: providerCode,
        encoderReference: `${providerCode}-${payload.roomId}`,
      };
    },
  };
}

export function createMockChannelManagerAdapter(providerCode = "mock-channel", displayName = "Mock Channel", behavior: MockBehavior = {}): ChannelManagerIntegrationAdapter {
  return {
    type: "channel_manager",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async syncAvailability(payload) {
      throwIfNeeded(providerCode, behavior, "syncAvailability");
      return {
        provider: providerCode,
        syncReference: `${providerCode}-${payload.idempotencyKey}`,
      };
    },
  };
}

export function createMockRevenueManagementAdapter(providerCode = "mock-rms", displayName = "Mock RMS", behavior: MockBehavior = {}): RevenueManagementIntegrationAdapter {
  return {
    type: "revenue_management",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async recommendRate(payload) {
      throwIfNeeded(providerCode, behavior, "recommendRate");
      return {
        provider: providerCode,
        recommendationId: `${providerCode}-${payload.from}-${payload.to}`,
        summary: `Recommend ADR uplift for occupancy ${payload.occupancy}%.`,
      };
    },
  };
}

export function createMockHousekeepingAppAdapter(providerCode = "mock-hk", displayName = "Mock Housekeeping", behavior: MockBehavior = {}): HousekeepingAppIntegrationAdapter {
  return {
    type: "housekeeping_app",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async syncTask(payload) {
      throwIfNeeded(providerCode, behavior, "syncTask");
      return {
        provider: providerCode,
        externalTaskId: `${providerCode}-${payload.taskId}`,
      };
    },
  };
}

export function createMockFiscalPrinterAdapter(providerCode = "mock-fiscal", displayName = "Mock Fiscal", behavior: MockBehavior = {}): FiscalPrinterIntegrationAdapter {
  return {
    type: "fiscal_printer",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async printFiscalReceipt(payload) {
      throwIfNeeded(providerCode, behavior, "printFiscalReceipt");
      return {
        provider: providerCode,
        receiptReference: `${providerCode}-${payload.invoiceId}`,
      };
    },
  };
}

export function createMockIdScannerAdapter(providerCode = "mock-id", displayName = "Mock ID Scanner", behavior: MockBehavior = {}): IdScannerIntegrationAdapter {
  return {
    type: "id_scanner",
    providerCode,
    displayName,
    healthCheck: () => buildHealthCheck(providerCode, behavior),
    async scanDocument() {
      throwIfNeeded(providerCode, behavior, "scanDocument");
      return {
        provider: providerCode,
        documentNumber: "SCAN-1001",
        nationality: "German",
        dateOfBirth: "1989-04-12",
        fullName: "Anna Muller",
      };
    },
  };
}
